import { readItemField } from './item-field-reader';
import type { ItemFieldRow, ItemFieldDependencies } from './item-field-reader';
import { phpJsonString, phpJsonTruthy } from './php-json-values';
import { phpJsonFloat } from './php-decimal-values';
import { utf8Bytes } from './php-json-serialize';
import { putIntegerCompilePath } from './integer-compute-slots';
import { createInventoryTemplateIdentityResolver } from './inventory-template-identity';
import type { InventoryTemplateQuery } from './inventory-template-identity';
import { createStatusLayerReadContext } from './status-row-construction';
import type { StatusOriginLayerDependencies } from './status-row-construction';
import { createInventoryEconomyReader } from './inventory-economy-reader';
import { calculateInventoryWeight, sumInventoryDescendantWeight, sumInventoryDescendantCostGp } from './inventory-totals';
import type { InventoryTotalsReaders } from './inventory-totals';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const has = (row: unknown,key: string): boolean => arrayLike(row) && Object.prototype.propertyIsEnumerable.call(row,key);
const own = (row: unknown,key: string): unknown => has(row,key) ? (row as Readonly<Record<string,unknown>>)[key] : undefined;
const uuid = (id: string): boolean => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(id);
function working(value: unknown): unknown {
    return arrayLike(value) ? Object.fromEntries(Object.entries(value).map(([key,child])=>[key,working(child)])) : value;
}
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key],i)=>key===String(i)) ? entries.map(([,child])=>clone(child))
        : Object.fromEntries(entries.map(([key,child])=>[key,clone(child)]));
}
function childContext(context: ItemFieldRow): ItemFieldRow {
    let next = 0;
    const out = Object.fromEntries(Object.entries(context).map(([key,value])=>{
        if (/^(?:0|-?[1-9]\d*)$/.test(key)) {
            const number = BigInt(key);
            if (number >= -9223372036854775808n && number <= 9223372036854775807n) return [String(next++),clone(value)];
        }
        return [key,clone(value)];
    }));
    Object.defineProperty(out,'possessor_type',{value:'inventory',writable:true,enumerable:true,configurable:true});
    return out;
}
function capacityOperand(value: unknown): number {
    if (arrayLike(value)) throw new TypeError('Capacity subtraction requires a numeric operand');
    if (typeof value === 'string') {
        const token = value.match(/^[\t\n\r\v\f ]*([+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?)/i)?.[1];
        if (token === undefined) throw new TypeError('Capacity subtraction requires a numeric operand');
        if (/^[+-]?\d+$/.test(token)) {
            const integer = BigInt(token);
            // PHP parses integer-form '-0' as integer zero before subtracting a
            // double; decimal/exponent negative zero remains a signed double.
            if (integer >= -9223372036854775808n && integer <= 9223372036854775807n) return Number(integer);
        }
    }
    return phpJsonFloat(value);
}
export interface InventoryContainerReaders extends InventoryTotalsReaders {
    itemField: (row: ItemFieldRow,key: string) => unknown;
    /** Actual child query results after the callee's UUID gate. Unknown results throw. */
    queryChildren: (id: string,visited: ItemFieldRow,context: ItemFieldRow) => ItemFieldRow;
    missingInventoryId?: () => void;
}
export function isInventoryContainer(row: ItemFieldRow | null,readers: Pick<InventoryContainerReaders,'itemField'|'hasLayer'>): boolean {
    if (row === null) return false;
    if (!arrayLike(row)) throw new TypeError('Container predicate requires a PHP array or null');
    const type = readers.itemField(row,'item_type');
    // PHP loose comparison with the nonnumeric string 'container'.
    if (type === true || type === 'container') return true;
    for (const key of ['carrying_capacity_cu_ft','carrying_capacity_cu_in','carrying_capacity_people','carrying_capacity_pounds','carrying_capacity_fixed_weight'])
        if (readers.hasLayer(row,key)) return true;
    return false;
}
/** Contiguous constructor container block, including the child query's typed
 * arguments and source UUID gate. Actual child reconstruction remains a dependency.
 */
export function compileInventoryContainer(input: ItemFieldRow,visited: ItemFieldRow,context: ItemFieldRow,readers: InventoryContainerReaders): ItemFieldRow {
    if (!arrayLike(input) || !arrayLike(visited) || !arrayLike(context)) throw new TypeError('Container compile requires PHP arrays');
    const row = working(input) as Record<string,unknown>;
    if (!isInventoryContainer(row,readers))
        return clone(row) as ItemFieldRow;
    const nextContext = childContext(context);
    if (!has(row,'inventory_id')) readers.missingInventoryId?.();
    const rawId = own(row,'inventory_id');
    if (rawId == null || arrayLike(rawId)) throw new TypeError('Child possessor ID requires a scalar string');
    const id = phpJsonString(rawId), children = uuid(id) ? readers.queryChildren(id,clone(visited) as ItemFieldRow,nextContext) : [];
    if (!arrayLike(children)) throw new TypeError('Child query must return a PHP array');
    if (Object.keys(children).length) putIntegerCompilePath(row,['inventory_in_container'],working(children));
    const held = readers.hasLayer(row,'carrying_capacity_fixed_weight') ? sumInventoryDescendantWeight(children,readers) : calculateInventoryWeight(children,readers);
    putIntegerCompilePath(row,['computed','weight','held'],held);
    const cost = sumInventoryDescendantCostGp(children,readers);
    putIntegerCompilePath(row,['computed','cost','contents_total_gp'],cost);
    const rawCapacity = own(row,'carrying_capacity_pounds');
    const byte = typeof rawCapacity === 'string' ? utf8Bytes(rawCapacity)[0] : undefined;
    const capacity = typeof rawCapacity === 'string' ? byte === undefined ? undefined : String.fromCharCode(byte) : own(rawCapacity,'0');
    if (phpJsonTruthy(capacity)) {
        putIntegerCompilePath(row,['computed','weight','capacity_remaining'],capacityOperand(capacity)-held);
    }
    return clone(row) as ItemFieldRow;
}
export interface InventoryContainerDependencies extends StatusOriginLayerDependencies, Omit<ItemFieldDependencies,'resolveCarrierRef'> {
    queryTemplateDefinition?: InventoryTemplateQuery;
    queryChildren: InventoryContainerReaders['queryChildren'];
}
export function createInventoryContainerCompiler(deps: InventoryContainerDependencies): (row: ItemFieldRow,visited?: ItemFieldRow,context?: ItemFieldRow) => ItemFieldRow {
    const context = createStatusLayerReadContext(deps),emit = (event: readonly unknown[]): void => { deps.trace?.(event); };
    const economy = createInventoryEconomyReader({resolveInventoryInstance:deps.resolveInventoryInstance,inventoryContext:context.inventoryContext});
    const identity = deps.resolveInventoryIdentity ?? (deps.queryTemplateDefinition ? createInventoryTemplateIdentityResolver(deps.queryTemplateDefinition) : undefined);
    const readers: InventoryContainerReaders = {computedValue:economy,layerValue:context.layerValue,hasLayer:context.hasLayer,
        missingRootContents:()=>emit(['missing-root','inventory_in_container']),missingInventoryId:()=>emit(['missing-root','inventory_id']),
        queryChildren:deps.queryChildren,itemField:(row,key)=>readItemField(row,key,{
            resolveCarrierRef:id=>{emit(['reference',id]);return deps.resolveCarrierRef(id);},
            ...(identity ? {resolveInventoryIdentity:(id:string)=>{emit(['inventory-identity',id]);return identity(id);}} : {}),
            ...(deps.resolveItemName ? {resolveItemName:(id:string)=>{emit(['name',id]);return deps.resolveItemName!(id);}} : {}),
        })};
    return (row,visited=[],inputContext=[])=>compileInventoryContainer(row,visited,inputContext,readers);
}
